import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import Immutable from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';
import ToggleSwitch from '@bibliocommons/toggle-switch';
import { Shelves } from '@bibliocommons/base-icons';
import { bibShape, shelfItemShape, userIdentityShape, ugcIOwnThisShape } from '@bibliocommons/bc-prop-types';
import { Dropdown, DropdownMenuItem, DropdownMenuHeader, DropdownMenuDivider } from '@bibliocommons/base-dropdown';
import { IconButton } from '@bibliocommons/base-buttons';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';
import BibScreenReaderDescription from 'app/components/shared/BibScreenReaderDescription';
import DeleteConfirmationModal from 'app/components/shelves/ShelfListItem/DeleteConfirmationModal';
import { DROPDOWN_ORDER } from 'app/constants/ShelfConstants';
import ShelfLink from 'app/components/shelves/ShelfLink';
import Context from '@bibliocommons/context';

import './OnShelfDropdown.scss';

function constructLabel(shelfName, briefInfo, iconOnly) {
  return (
    <span className="shelf-option">
      {!iconOnly && (
        <span aria-hidden>
          <FormattedMessage id={`label_shelf_${shelfName}`} />
        </span>
      )}
      <ScreenReaderMessage>
        <FormattedMessage
          id="ada_add_to_named_shelf"
          values={{
            title: <BibScreenReaderDescription bibBrief={briefInfo} />,
            shelf: shelfName
          }}
        />
      </ScreenReaderMessage>
    </span>
  );
}

export class OnShelfDropdown extends React.PureComponent {
  removeModalKey = `remove-from-shelf-${this.props.bib.get('id')}`;

  moveToShelf = newShelfName => {
    const { bib, shelfItem, reciprocalRank } = this.props;

    this.props.shelfActions.updateShelfItems({
      shelfName: shelfItem.get('shelf'),
      newShelfName,
      shelfItemIds: [shelfItem.get('id')],
      metadataIds: [bib.get('id')],
      privateItem: shelfItem.get('privateItem'),
      reciprocalRank
    });

    if (typeof this.props.trackContentCollectionMove === 'function') {
      this.props.trackContentCollectionMove({
        bibId: bib.get('id'),
        newShelf: newShelfName,
        privateItem: shelfItem.get('privateItem')
      });
    }
  };

  toggleIOwnThis = () => {
    const metadataId = this.props.bib.get('id');
    const { iOwnThis } = this.props;

    if (iOwnThis) {
      this.props.iOwnThisActions.removeIOwnThisSetting(metadataId, iOwnThis.get('id'));
    } else {
      this.props.iOwnThisActions.createIOwnThisSetting(metadataId);
    }
  };

  togglePrivate = () => {
    const { bib, shelfItem, reciprocalRank } = this.props;
    const isPrivate = shelfItem.get('privateItem');

    this.props.shelfActions.updateShelfItems({
      privateItem: !isPrivate,
      shelfName: shelfItem.get('shelf'),
      newShelfName: shelfItem.get('shelf'),
      shelfItemIds: [shelfItem.get('id')],
      metadataIds: [bib.get('id')],
      reciprocalRank
    });
  };

  openRemovalModal = () => {
    this.props.modalActions.openModal(this.removeModalKey);
  };

  renderMoveToShelfOptions = () => {
    const { bib, shelfItem, iconOnly } = this.props;

    const briefInfo = bib.get('briefInfo');
    const currentShelf = shelfItem.get('shelf');
    const dropdownOrder = iconOnly ? DROPDOWN_ORDER : DROPDOWN_ORDER.filter(shelfName => shelfName !== currentShelf);

    return dropdownOrder.map(shelfName => (
      <DropdownMenuItem
        item={Immutable.Map({
          label: constructLabel(shelfName, briefInfo),
          value: shelfName,
          selected: shelfName === currentShelf
        })}
        key={shelfName}
        handleClick={() => this.moveToShelf(shelfName)}
        selectable={iconOnly}
      />
    ));
  };

  renderLabel = () => {
    const shelfName = this.props.shelfItem.get('shelf');
    const briefInfo = this.props.bib.get('briefInfo');
    const { iconOnly } = this.props;

    return (
      <span className="dropdown-label">
        <Shelves className={iconOnly && 'icon-only'} />
        {constructLabel(shelfName, briefInfo, iconOnly)}
      </span>
    );
  };

  renderShelfLink() {
    return (
      <DropdownMenuItem>
        <ShelfLink userId={this.props.user.get('id')} shelfName={this.props.shelfItem.get('shelf')}>
          <FormattedMessage id="go_to_shelf" />
        </ShelfLink>
      </DropdownMenuItem>
    );
  }

  renderRemoveButton() {
    return (
      <DropdownMenuItem
        handleClick={this.openRemovalModal}
        item={Immutable.Map({
          label: <FormattedMessage id="button_remove_from_shelves" />,
          value: 'remove-from-shelf'
        })}
      />
    );
  }

  renderPrivateSwitch() {
    return (
      <DropdownMenuItem closeOnClick={false}>
        <ToggleSwitch
          id="private-item"
          active={!!this.props.shelfItem.get('privateItem')}
          handleClick={this.togglePrivate}
          activeText={<FormattedMessage id="mark_as_private" />}
          inactiveText={<FormattedMessage id="mark_as_private" />}
        />
      </DropdownMenuItem>
    );
  }

  renderIOwnThisSwitch() {
    return (
      <DropdownMenuItem closeOnClick={false}>
        <ToggleSwitch
          id="i-own-this"
          active={!!this.props.iOwnThis}
          handleClick={this.toggleIOwnThis}
          activeText={<FormattedMessage id="button_i_own_this" />}
          inactiveText={<FormattedMessage id="button_i_own_this" />}
        />
      </DropdownMenuItem>
    );
  }

  render() {
    const {
      bib,
      rounded,
      disabled,
      right,
      shelfTransactions,
      shelfItem,
      modalActions,
      shelfActions,
      handleDeleteSuccess,
      intl,
      iconOnly
    } = this.props;
    const transactions = shelfTransactions.get(bib.get('id')) || Immutable.Map();
    const isMoving = transactions.get('isMoving', false);
    const isRemoving = transactions.get('isRemoving', false);
    const isUpdatingPrivacy = transactions.get('isUpdatingPrivacy', false);
    const formattedShelfName = intl.formatMessage({
      id: `label_shelf_${shelfItem.get('shelf')}`
    });

    return (
      <div className="cp-on-shelf-dropdown">
        <Dropdown
          block={!iconOnly}
          right={right}
          rounded={rounded}
          disabled={disabled}
          isLoading={isMoving || isUpdatingPrivacy}
          dataKey="on-shelf-dropdown"
          aria-label={intl.formatMessage({ id: 'item_on_shelf_cta' }, { shelfName: formattedShelfName })}
          renderLabel={this.renderLabel}
          buttonTag={iconOnly && IconButton}
        >
          {!iconOnly && (
            <DropdownMenuHeader>
              <FormattedMessage id="move_to_shelf" />
            </DropdownMenuHeader>
          )}
          {this.renderMoveToShelfOptions()}
          <DropdownMenuDivider />
          {!this.context.mobileApp && this.renderShelfLink()}
          {this.renderRemoveButton()}
          <DropdownMenuDivider />
          {this.renderPrivateSwitch()}
          {this.renderIOwnThisSwitch()}
        </Dropdown>

        <DeleteConfirmationModal
          modalActions={modalActions}
          shelfActions={shelfActions}
          modalKey={this.removeModalKey}
          items={Immutable.List.of(shelfItem)}
          isLoading={isRemoving}
          postSuccess={handleDeleteSuccess}
        />
      </div>
    );
  }
}

OnShelfDropdown.propTypes = {
  bib: bibShape.isRequired,
  shelfItem: shelfItemShape.isRequired,
  iOwnThis: ugcIOwnThisShape,
  shelfTransactions: ImmutablePropTypes.map,
  rounded: PropTypes.bool,
  disabled: PropTypes.bool,
  right: PropTypes.bool,
  user: userIdentityShape,
  reciprocalRank: PropTypes.number,
  handleDeleteSuccess: PropTypes.func,
  shelfActions: PropTypes.objectOf(PropTypes.func).isRequired,
  iOwnThisActions: PropTypes.objectOf(PropTypes.func).isRequired,
  modalActions: PropTypes.objectOf(PropTypes.func).isRequired,
  iconOnly: PropTypes.bool,
  trackContentCollectionMove: PropTypes.func
};

OnShelfDropdown.contextType = Context;

export default injectIntl(OnShelfDropdown, { forwardRef: true });
